import type { Layout } from "@klarity/themes";
import clsx from "clsx";
import { useState } from "react";
import { Button } from "../../../components/ui/Button";
import { Field, TextArea, TextInput } from "../../../components/ui/Field";
import { usePrompt } from "../../../components/ui/PromptDialog";

const KEY = /^[a-z][a-z0-9-]{0,31}$/;
const PARTS: Array<{
  key: keyof Pick<Layout, "cover" | "titleBlock" | "header" | "footer" | "css">;
  label: string;
  rows: number;
  optional: boolean;
}> = [
  { key: "cover", label: "Cover template", rows: 10, optional: true },
  { key: "titleBlock", label: "Title block template", rows: 6, optional: true },
  { key: "header", label: "Header template", rows: 5, optional: false },
  { key: "footer", label: "Footer template", rows: 5, optional: false },
  { key: "css", label: "Layout CSS", rows: 14, optional: false },
];

export function LayoutEditor({
  layouts,
  onChange,
  readOnly = false,
}: {
  layouts: Record<string, Layout>;
  onChange: (next: Record<string, Layout>) => void;
  readOnly?: boolean;
}) {
  const keys = Object.keys(layouts);
  const [current, setCurrent] = useState(keys[0] ?? "");
  const { ask, element } = usePrompt();
  const layout = layouts[current];
  const set = (patch: Partial<Layout>) => {
    if (!layout) return;
    const next: Layout = { ...layout, ...patch };
    if (next.cover === "") delete next.cover;
    if (next.titleBlock === "") delete next.titleBlock;
    onChange({ ...layouts, [current]: next });
  };
  const add = async () => {
    const key = await ask({
      title: "Add a layout",
      label: "Key",
      confirmLabel: "Add",
      validate: (v) =>
        !KEY.test(v.trim())
          ? "Use lowercase letters, digits and hyphens, starting with a letter."
          : layouts[v.trim()]
            ? "A layout with this key exists."
            : null,
    });
    if (!key || !layout) return;
    const k = key.trim();
    onChange({ ...layouts, [k]: { ...layout, key: k, name: k } });
    setCurrent(k);
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-1.5">
        {keys.map((k) => (
          <button
            key={k}
            type="button"
            aria-pressed={k === current}
            onClick={() => setCurrent(k)}
            className={clsx(
              "rounded-md border px-3 py-1 font-mono text-xs",
              k === current ? "border-green bg-green-wash text-green-ink" : "border-line hover:border-line-strong",
            )}
          >
            {k}
          </button>
        ))}
        {readOnly ? null : (
          <Button size="sm" variant="ghost" onClick={() => void add()}>
            Add layout
          </Button>
        )}
      </div>
      <p className="text-xs text-ink-3">
        Fields:{" "}
        <code>
          {
            "{{code}} {{subtitle}} {{docType}} {{client}} {{date}} {{version}} {{classification}} {{tagline}} {{title}} {{logo.cover}} {{logo.header}}"
          }
        </code>
        . Show a part only when a field has a value with <code>{"{{#client}}…{{/client}}"}</code>. No other logic.
      </p>
      {layout ? (
        <div className="grid gap-4">
          <Field label="Layout name">
            {(id) => (
              <TextInput
                id={id}
                readOnly={readOnly}
                value={layout.name}
                onChange={(e) => set({ name: e.target.value })}
              />
            )}
          </Field>
          {PARTS.map((p) => (
            <Field key={p.key} label={p.label} hint={p.optional ? "Optional. Leave empty for none." : undefined}>
              {(id, describedBy) => (
                <TextArea
                  id={id}
                  aria-describedby={describedBy}
                  readOnly={readOnly}
                  rows={p.rows}
                  spellCheck={false}
                  value={layout[p.key] ?? ""}
                  onChange={(e) => set({ [p.key]: e.target.value } as Partial<Layout>)}
                />
              )}
            </Field>
          ))}
        </div>
      ) : null}
      {element}
    </div>
  );
}
